<script setup lang="ts">
import { computed, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { getCurrentShapeInputComponent } from "@/components/shapes/ShapeHelper";
import ValidationMessage from "./UI/ValidationMessage.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";

const store = useStore();
const shape = computed(() => store.getters.shape);
const dimensions = computed(() => store.getters.dimensions);
const modifiers = computed(() => store.getters.modifiers);
const hasGroupedDimensionValidatorErrors = computed(
	() => store.getters.hasGroupedDimensionValidatorErrors
);
const getGroupedDimensionValidationMessage = computed(
	() => store.getters.getGroupedDimensionValidationMessage
);
const hasProblematicCorners = computed(() => store.getters.hasProblematicCorners);
const validationDimensionMessages = computed(() => {
	const validators = store.getters.getDimensionValidatorErrors;
	return validators.map((validator) => {
		return {
			key: validator.field,
			id: `measurements-${validator.field}`,
			message: validator.getMessage(),
		};
	});
});

document.addEventListener('focusin', (event) => store.commit('updateFocusedElement', event));
document.addEventListener('focusout', () => store.commit('updateFocusedElement', ''));

onBeforeUnmount(() => {
	document.removeEventListener('focusout', () => store.commit('updateFocusedElement'));
	document.removeEventListener('focusin', () => store.commit('updateFocusedElement'));
});

const currentProps = computed(() => {
	switch (shape.value) {
		case "rectangle":
		case "rounded-rectangle":
		case "circle":
		case "half-circle":
		case "quarter-circle":
		case "egg":
		case "ellipse":
		case "flat-oval":
		case "sloped-rectangle":
		case "sloped-rectangle-flip-horizontal":
		case "sloped-right-rectangle":
		case "sloped-right-rectangle-flip-vertical":
		case "right-angled-triangle":
		case "right-angled-triangle-mirrored":
		case "isosceles-triangle":
		case "trapezoid":
		case "hexagon":
		case "octagon":
		case "polygon":
		case "heart":
		case "arrow":
		case "star":
		case "text":
		case "droplet":
		case "kite":
		case "barrel":
		case "jewel":
		case "parallelogram-flip-horizontal":
		case "parallelogram":
		case "arched-rectangle":
			return {
				action: "updatePlateDimension",
				modifierAction: "updateModifier",
				isCutout: false,
				prefix: '',
			};
		default:
			return {
				value: dimensions.value,
				modifiers: modifiers.value,
			};
	}
});
</script>

<template>
	<div>
		<component
			:is="getCurrentShapeInputComponent(shape)"
			v-bind="currentProps"
		/>

		<message-container>
			<validation-message
				v-for="field in validationDimensionMessages"
				:key="field.key + '-validation-message'"
				:input-id="field.id"
			>
				{{ field.message }}
			</validation-message>

			<validation-message
				v-if="hasProblematicCorners"
				type="notice"
			>
				{{ $i18n.problematicCornersNotice }}
			</validation-message>

			<validation-message
				v-if="hasGroupedDimensionValidatorErrors"
				input-id="grouped-validation-message"
			>
				{{ getGroupedDimensionValidationMessage }}
			</validation-message>
		</message-container>
	</div>
</template>
